import { useEffect, useState } from "react";
import { auth } from "@/lib/auth";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { Lock } from "lucide-react";
import AdminDashboard from "@/components/AdminDashboard";

const API = process.env.REACT_APP_BACKEND_URL;

const Table = ({ title, testid, rows, cols }) => (
    <div className="mt-10">
        <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-cream md:text-2xl">
            {title}
            <span className="text-violet">.</span>
        </h3>
        <div className="mt-4 overflow-x-auto border border-white/10" data-testid={testid}>
            {rows.length === 0 ? (
                <p className="p-6 font-mono text-[10px] tracking-[0.3em] text-cream/40">—</p>
            ) : (
                <table className="w-full text-left font-mono text-[10px] tracking-[0.1em]">
                    <thead>
                        <tr className="border-b border-white/10 text-cream/40">
                            {cols.map((c) => (
                                <th key={c.k} className="px-4 py-3 font-normal uppercase">{c.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-b border-white/5 text-cream/80">
                                {cols.map((c) => (
                                    <td key={c.k} className="px-4 py-3 align-top">
                                        {c.render ? c.render(r) : String(r[c.k] ?? "—")}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    </div>
);

export default function AdminPage() {
    const { lang } = useLang();
    const [state, setState] = useState(auth.state);
    const [data, setData] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [projects, setProjects] = useState([]);
    const [vipTickets, setVipTickets] = useState([]);
    const [payments, setPayments] = useState([]);
    const [newProject, setNewProject] = useState({ name: "", email: "", project_type: "MUSIC VIDEO" });
    useEffect(() => auth.subscribe(setState), []);

    useEffect(() => {
        if (!state.user?.is_admin) return;
        (async () => {
            const opts = { credentials: "include" };
            const [regs, contacts, bookings, subs, projs, vips, pays] = await Promise.all([
                fetch(`${API}/api/admin/registrations`, opts).then((r) => r.json()),
                fetch(`${API}/api/admin/contacts`, opts).then((r) => r.json()),
                fetch(`${API}/api/admin/bookings`, opts).then((r) => r.json()),
                fetch(`${API}/api/admin/submissions`, opts).then((r) => r.json()),
                fetch(`${API}/api/admin/projects`, opts).then((r) => r.json()),
                fetch(`${API}/api/admin/vip-tickets`, opts).then((r) => r.json()).catch(() => ({ tickets: [] })),
                fetch(`${API}/api/admin/payments`, opts).then((r) => r.json()).catch(() => ({ payments: [] })),
            ]);
            setData({
                registrations: regs.registrations || [],
                contacts: contacts.contacts || [],
                bookings: bookings.bookings || [],
            });
            setSubmissions(subs.submissions || []);
            setProjects(projs.projects || []);
            setVipTickets(vips.tickets || []);
            setPayments(pays.payments || []);
        })();
    }, [state.user]);

    const refresh = () => {
        const opts = { credentials: "include" };
        fetch(`${API}/api/admin/projects`, opts).then((r) => r.json()).then((d) => setProjects(d.projects || []));
        fetch(`${API}/api/admin/submissions`, opts).then((r) => r.json()).then((d) => setSubmissions(d.submissions || []));
    };

    return (
        <section
            data-testid="admin-page"
            className="min-h-screen bg-ink px-5 pb-24 pt-32 text-cream md:px-10"
        >
            <div className="mx-auto max-w-[1200px]">
                <p className="flex items-center gap-3 font-mono text-[10px] tracking-[0.35em] text-violet">
                    <Lock size={12} /> {tx(lang, T.admin.label)}
                </p>
                <h1 className="mt-6 font-display text-4xl font-extrabold uppercase tracking-tight md:text-6xl">
                    {tx(lang, T.admin.title)}
                    <span className="text-violet">.</span>
                </h1>

                {state.status === "checking" && (
                    <p className="mt-10 font-mono text-[10px] tracking-[0.3em] text-cream/40">…</p>
                )}
                {state.status === "guest" && (
                    <button
                        data-testid="admin-google-btn"
                        onClick={() => auth.login()}
                        className="mt-10 border border-violet/60 px-6 py-3 font-mono text-[11px] tracking-[0.3em] text-violet transition-all hover:bg-violet hover:text-cream"
                    >
                        {tx(lang, T.admin.login)}
                    </button>
                )}
                {state.status === "authed" && !state.user.is_admin && (
                    <p
                        data-testid="admin-denied"
                        className="mt-10 font-mono text-[10px] tracking-[0.3em] text-blood"
                    >
                        {tx(lang, T.admin.denied)} — {state.user.email}
                    </p>
                )}
                {state.status === "authed" && state.user.is_admin && (
                    <>
                        <p className="mt-4 font-mono text-[10px] tracking-[0.3em] text-cream/40">
                            {state.user.email}
                        </p>
                        {/* dashboard animé — vue d'ensemble du site */}
                        <div className="mt-10">
                            <AdminDashboard />
                        </div>
                        {data && (
                            <>
                                <Table
                                    title={tx(lang, T.admin.bookings)}
                                    testid="admin-bookings-table"
                                    rows={data.bookings}
                                    cols={[
                                        { k: "date", label: "DATE" },
                                        { k: "name", label: "NOM" },
                                        { k: "email", label: "EMAIL" },
                                        { k: "project_type", label: "PROJET" },
                                        {
                                            k: "budget",
                                            label: "BUDGET",
                                            render: (r) =>
                                                r.vip ? `${r.budget} CHF — VIP` : `${r.budget} CHF`,
                                        },
                                    ]}
                                />
                                <Table
                                    title={lang === "fr" ? "ACOMPTES (STRIPE)" : "DEPOSITS (STRIPE)"}
                                    testid="admin-payments-table"
                                    rows={payments}
                                    cols={[
                                        {
                                            k: "amount",
                                            label: "MONTANT",
                                            render: (r) => `${(r.amount / 100).toFixed(0)} ${(r.currency || "").toUpperCase()}`,
                                        },
                                        { k: "payment_status", label: "STATUT" },
                                        { k: "booking_ref", label: "RÉSERVATION" },
                                        {
                                            k: "created_at",
                                            label: "DATE",
                                            render: (r) => (r.created_at || "").slice(0, 10),
                                        },
                                    ]}
                                />
                                <Table
                                    title={lang === "fr" ? "BILLETS VIP RÉCLAMÉS" : "CLAIMED VIP PASSES"}
                                    testid="admin-vip-table"
                                    rows={vipTickets}
                                    cols={[
                                        { k: "code", label: "CODE" },
                                        {
                                            k: "first_name",
                                            label: "NOM",
                                            render: (r) => `${r.first_name} ${r.last_name}`,
                                        },
                                        { k: "email", label: "EMAIL" },
                                        {
                                            k: "created_at",
                                            label: "DATE",
                                            render: (r) => (r.created_at || "").slice(0, 10),
                                        },
                                    ]}
                                />
                                <Table
                                    title={tx(lang, T.admin.registrations)}
                                    testid="admin-registrations-table"
                                    rows={data.registrations}
                                    cols={[
                                        { k: "first_name", label: "PRÉNOM" },
                                        { k: "last_name", label: "NOM" },
                                        { k: "email", label: "EMAIL" },
                                        { k: "phone", label: "TÉL" },
                                    ]}
                                />
                                <Table
                                    title={tx(lang, T.admin.contacts)}
                                    testid="admin-contacts-table"
                                    rows={data.contacts}
                                    cols={[
                                        { k: "name", label: "NOM" },
                                        { k: "email", label: "EMAIL" },
                                        { k: "project_type", label: "PROJET" },
                                        { k: "message", label: "MESSAGE" },
                                    ]}
                                />
                            </>
                        )}
                        {/* radio submissions */}
                        <div className="mt-14">
                            <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-cream md:text-2xl">
                                {tx(lang, T.admin.submissions)}<span className="text-violet">.</span>
                            </h3>
                            <div className="mt-4 overflow-x-auto border border-white/10" data-testid="admin-submissions-table">
                                {submissions.length === 0 ? (
                                    <p className="p-6 font-mono text-[10px] tracking-[0.3em] text-cream/40">—</p>
                                ) : (
                                    <table className="w-full text-left font-mono text-[10px] tracking-[0.1em]">
                                        <thead>
                                            <tr className="border-b border-white/10 text-cream/40">
                                                <th className="px-4 py-3 font-normal uppercase">ARTISTE</th>
                                                <th className="px-4 py-3 font-normal uppercase">TITRE</th>
                                                <th className="px-4 py-3 font-normal uppercase">LIEN</th>
                                                <th className="px-4 py-3 font-normal uppercase">STATUT</th>
                                                <th className="px-4 py-3 font-normal uppercase" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {submissions.map((s) => (
                                                <tr key={s.submission_id} className="border-b border-white/5 text-cream/80">
                                                    <td className="px-4 py-3">{s.artist}</td>
                                                    <td className="px-4 py-3">{s.title}</td>
                                                    <td className="max-w-[200px] truncate px-4 py-3">{s.link}</td>
                                                    <td className="px-4 py-3">{s.status === "approved" ? "✓" : tx(lang, T.admin.pending)}</td>
                                                    <td className="px-4 py-3">
                                                        {s.status !== "approved" && (
                                                            <button
                                                                data-testid={`admin-approve-${s.submission_id}`}
                                                                onClick={async () => {
                                                                    await fetch(`${API}/api/admin/submissions/${s.submission_id}/approve`, { method: "POST", credentials: "include" });
                                                                    refresh();
                                                                }}
                                                                className="border border-gold/50 px-3 py-1 font-mono text-[9px] tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-ink"
                                                            >
                                                                {tx(lang, T.admin.approve)}
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        {/* client projects */}
                        <div className="mt-14">
                            <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-cream md:text-2xl">
                                {tx(lang, T.admin.projects)}<span className="text-violet">.</span>
                            </h3>
                            <div className="mt-4 flex flex-wrap gap-3 border border-white/10 p-5">
                                <input data-testid="admin-project-name" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} placeholder={tx(lang, T.admin.newProject)} className="border-b border-white/15 bg-transparent py-2 font-mono text-[10px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30" />
                                <input data-testid="admin-project-email" value={newProject.email} onChange={(e) => setNewProject({ ...newProject, email: e.target.value })} placeholder={tx(lang, T.admin.clientEmail)} className="border-b border-white/15 bg-transparent py-2 font-mono text-[10px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30" />
                                <button
                                    data-testid="admin-project-add"
                                    onClick={async () => {
                                        await fetch(`${API}/api/admin/projects`, {
                                            method: "POST",
                                            headers: { "Content-Type": "application/json" },
                                            credentials: "include",
                                            body: JSON.stringify(newProject),
                                        });
                                        setNewProject({ name: "", email: "", project_type: "MUSIC VIDEO" });
                                        refresh();
                                    }}
                                    className="border border-violet/60 px-4 py-2 font-mono text-[9px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream"
                                >
                                    {tx(lang, T.admin.addProject)}
                                </button>
                            </div>
                            <div className="mt-4 overflow-x-auto border border-white/10" data-testid="admin-projects-table">
                                {projects.length === 0 ? (
                                    <p className="p-6 font-mono text-[10px] tracking-[0.3em] text-cream/40">—</p>
                                ) : (
                                    <table className="w-full text-left font-mono text-[10px] tracking-[0.1em]">
                                        <thead>
                                            <tr className="border-b border-white/10 text-cream/40">
                                                <th className="px-4 py-3 font-normal uppercase">PROJET</th>
                                                <th className="px-4 py-3 font-normal uppercase">CLIENT</th>
                                                <th className="px-4 py-3 font-normal uppercase">ÉTAPE</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {projects.map((p) => (
                                                <tr key={p.project_id} className="border-b border-white/5 text-cream/80">
                                                    <td className="px-4 py-3">{p.name}</td>
                                                    <td className="px-4 py-3">{p.email}</td>
                                                    <td className="px-4 py-3">
                                                        <select
                                                            data-testid={`admin-stage-${p.project_id}`}
                                                            value={p.stage}
                                                            onChange={async (e) => {
                                                                await fetch(`${API}/api/admin/projects/${p.project_id}/stage`, {
                                                                    method: "POST",
                                                                    headers: { "Content-Type": "application/json" },
                                                                    credentials: "include",
                                                                    body: JSON.stringify({ stage: Number(e.target.value) }),
                                                                });
                                                                refresh();
                                                            }}
                                                            className="bg-ink font-mono text-[10px] outline-none"
                                                        >
                                                            {T.admin.projectStages.map((s, i) => (
                                                                <option key={s.en} value={i}>{s[lang]}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        <button
                            data-testid="admin-logout-btn"
                            onClick={() => auth.logout()}
                            className="mt-12 font-mono text-[10px] tracking-[0.3em] text-cream/40 transition-colors hover:text-cream"
                        >
                            {tx(lang, T.admin.logout)}
                        </button>
                    </>
                )}
            </div>
        </section>
    );
}
